import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import SupportChatWidget from './components/SupportChatWidget';
import './App.css';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import RegisterPage from './pages/RegisterPage';
import AthleteDashboard from './pages/AthleteDashboard';
import TodaysPlanDashboard from './pages/TodaysPlanDashboard';
import SupporterDashboard from './pages/SupporterDashboard';
import OrganizationDashboard from './pages/OrganizationDashboard';
import OrganizationRegisterPage from './pages/OrganizationRegisterPage';
import JoinOrganizationPage from './pages/JoinOrganizationPage';
import WorkoutsPage from './pages/WorkoutsPage';
import NutritionPage from './pages/NutritionPage';
import NutritionProPage from './pages/NutritionProPage';
import RecoveryPage from './pages/RecoveryPage';
import SchedulePage from './pages/SchedulePage';
import SettingsPage from './pages/SettingsPage';
import PricingPage from './pages/PricingPage';
import AuthCallback from './pages/AuthCallback';
import AdminDashboard from './pages/AdminDashboard';
import BadgesPage from './pages/BadgesPage';
import ExerciseLibraryPage from './pages/ExerciseLibraryPage';
import AcademicsPage from './pages/AcademicsPage';
import GroceryListPage from './pages/GroceryListPage';
import ShareAuraPage from './pages/ShareAuraPage';
import TrainingProgramPage from './pages/TrainingProgramPage';
import EquipmentPage from './pages/EquipmentPage';
import AthleteDetailPage from './pages/AthleteDetailPage';
import FamilyMealPlanPage from './pages/FamilyMealPlanPage';
import WeeklyMealPlanPage from './pages/WeeklyMealPlanPage';
import OnboardingPage from './pages/OnboardingPage';
import GameDayRoutinePage from './pages/GameDayRoutinePage';
import FamilySchedulePage from './pages/FamilySchedulePage';
import FamilyWorkoutsPage from './pages/FamilyWorkoutsPage';
import AthleteProfilePage from './pages/AthleteProfilePage';
import WeeklySummaryPage from './pages/WeeklySummaryPage';
import LeaderboardPage from './pages/LeaderboardPage';
import OrganizationSettingsPage from './pages/OrganizationSettingsPage';
import AlertSettingsPage from './pages/AlertSettingsPage';
import AthleteReadinessPage from './pages/AthleteReadinessPage';
import NilDashboardPage from './pages/NilDashboardPage';
import TransferPortalPage from './pages/TransferPortalPage';
import WeeklyChallengesPage from './pages/WeeklyChallengesPage';
import CoachAssignmentsPage from './pages/CoachAssignmentsPage';
import ReportIssuePage from './pages/ReportIssuePage';
import MatrixLeaderboardPage from './pages/MatrixLeaderboardPage';
import ChatAnalyticsPage from './pages/ChatAnalyticsPage';
// New Admin Pages
import AdminOverviewPage from './pages/AdminOverviewPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminUserDetailPage from './pages/AdminUserDetailPage';
import AdminOrganizationsPage from './pages/AdminOrganizationsPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import GoalsPage from './pages/GoalsPage';
import TrainingProfilePage from './pages/TrainingProfilePage';
import ExternalWorkoutPage from './pages/ExternalWorkoutPage';
import SupporterAthleteDetailPage from './pages/SupporterAthleteDetailPage';
import SupporterLeaderboardPage from './pages/SupporterLeaderboardPage';
import HelpCenterPage from './pages/HelpCenterPage';
import CalendarHubPage from './pages/CalendarHubPage';
import AdminSupportTicketsPage from './pages/AdminSupportTicketsPage';
import TeamLeaderboardPage from './pages/TeamLeaderboardPage';
import { ExerciseLibraryProvider } from './components/context/ExerciseLibraryContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('aura_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUser(response.data);
      
      // Check subscription status
      try {
        const subResponse = await axios.get(`${API}/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setSubscription(subResponse.data);
      } catch (e) {
        // Subscription endpoint might not exist yet, that's ok
      }
    } catch (error) {
      localStorage.removeItem('aura_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    localStorage.setItem('aura_token', response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data, { withCredentials: true });
    localStorage.setItem('aura_token', response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // Ignore errors
    }
    localStorage.removeItem('aura_token');
    setUser(null);
    setSubscription(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      subscription,
      login, 
      register, 
      loginWithGoogle, 
      logout, 
      updateUser,
      checkAuth 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = ['athlete', 'supporter', 'org_admin'], skipOnboarding = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-aura-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-aura-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-aura-text-muted font-heading uppercase tracking-wider">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect athletes who haven't completed onboarding (unless we're already on onboarding page)
  if (!skipOnboarding && user.role === 'athlete' && !user.onboarding_completed && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  if (!allowedRoles.includes(user.role)) {
    // Redirect based on role
    if (user.role === 'supporter') {
      return <Navigate to="/supporter" replace />;
    } else if (user.role === 'org_admin') {
      return <Navigate to="/organization" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router Component
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/onboarding" element={
        <ProtectedRoute allowedRoles={['athlete', 'supporter']} skipOnboarding={true}>
          <OnboardingPage />
        </ProtectedRoute>
      } />

      {/* Athlete Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <TodaysPlanDashboard />
        </ProtectedRoute>
      } />
      <Route path="/dashboard-old" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <AthleteDashboard />
        </ProtectedRoute>
      } />
      <Route path="/workouts" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <WorkoutsPage />
        </ProtectedRoute>
      } />
      <Route path="/nutrition" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <NutritionPage />
        </ProtectedRoute>
      } />
      <Route path="/nutrition-pro" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <NutritionProPage />
        </ProtectedRoute>
      } />
      <Route path="/goals" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <GoalsPage />
        </ProtectedRoute>
      } />
      <Route path="/training-profile" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <TrainingProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/external-workout" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <ExternalWorkoutPage />
        </ProtectedRoute>
      } />
      <Route path="/recovery" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <RecoveryPage />
        </ProtectedRoute>
      } />
      <Route path="/schedule" element={
        <ProtectedRoute allowedRoles={['athlete', 'supporter']}>
          <SchedulePage />
        </ProtectedRoute>
      } />
      <Route path="/calendar-hub" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <CalendarHubPage />
        </ProtectedRoute>
      } />
      <Route path="/game-day" element={
        <ProtectedRoute allowedRoles={['athlete', 'supporter']}>
          <GameDayRoutinePage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute allowedRoles={['athlete', 'supporter']}>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/badges" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <BadgesPage />
        </ProtectedRoute>
      } />
      <Route path="/exercises" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <ExerciseLibraryPage />
        </ProtectedRoute>
      } />
      <Route path="/academics" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <AcademicsPage />
        </ProtectedRoute>
      } />
      <Route path="/grocery-list" element={
        <ProtectedRoute allowedRoles={['athlete', 'supporter']}>
          <GroceryListPage />
        </ProtectedRoute>
      } />
      <Route path="/share" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <ShareAuraPage />
        </ProtectedRoute>
      } />
      <Route path="/my-profile" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <AthleteProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/weekly-summary" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <WeeklySummaryPage />
        </ProtectedRoute>
      } />
      <Route path="/leaderboard" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <LeaderboardPage />
        </ProtectedRoute>
      } />
      <Route path="/training-program" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <TrainingProgramPage />
        </ProtectedRoute>
      } />
      <Route path="/equipment" element={
        <ProtectedRoute allowedRoles={['athlete', 'supporter']}>
          <EquipmentPage />
        </ProtectedRoute>
      } />

      {/* Supporter Routes */}
      <Route path="/supporter" element={
        <ProtectedRoute allowedRoles={['supporter']}>
          <SupporterDashboard />
        </ProtectedRoute>
      } />
      <Route path="/athlete/:athleteId" element={
        <ProtectedRoute allowedRoles={['supporter']}>
          <AthleteDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/supporter/athlete/:athleteId" element={
        <ProtectedRoute allowedRoles={['supporter']}>
          <SupporterAthleteDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/family/meal-plan" element={
        <ProtectedRoute allowedRoles={['supporter']}>
          <FamilyMealPlanPage />
        </ProtectedRoute>
      } />
      <Route path="/family/schedule" element={
        <ProtectedRoute allowedRoles={['supporter']}>
          <FamilySchedulePage />
        </ProtectedRoute>
      } />
      <Route path="/family/workouts" element={
        <ProtectedRoute allowedRoles={['supporter']}>
          <FamilyWorkoutsPage />
        </ProtectedRoute>
      } />
      <Route path="/supporter/leaderboard" element={
        <ProtectedRoute allowedRoles={['supporter']}>
          <SupporterLeaderboardPage />
        </ProtectedRoute>
      } />
      <Route path="/meal-plan" element={
        <ProtectedRoute allowedRoles={['athlete', 'supporter']}>
          <WeeklyMealPlanPage />
        </ProtectedRoute>
      } />
      <Route path="/readiness" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <AthleteReadinessPage />
        </ProtectedRoute>
      } />
      <Route path="/nil-dashboard" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <NilDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/transfer-portal" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <TransferPortalPage />
        </ProtectedRoute>
      } />
      <Route path="/weekly-challenges" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <WeeklyChallengesPage />
        </ProtectedRoute>
      } />

      {/* Organization Routes */}
      <Route path="/organization/register" element={<OrganizationRegisterPage />} />
      <Route path="/organization/join" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <JoinOrganizationPage />
        </ProtectedRoute>
      } />
      <Route path="/organization" element={
        <ProtectedRoute allowedRoles={['org_admin']}>
          <OrganizationDashboard />
        </ProtectedRoute>
      } />
      <Route path="/organization/settings" element={
        <ProtectedRoute allowedRoles={['org_admin']}>
          <OrganizationSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/organization/leaderboard" element={
        <ProtectedRoute allowedRoles={['org_admin']}>
          <TeamLeaderboardPage />
        </ProtectedRoute>
      } />

      {/* Supporter Routes */}
      <Route path="/alerts/settings" element={
        <ProtectedRoute allowedRoles={['supporter']}>
          <AlertSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/coach/assignments" element={
        <ProtectedRoute allowedRoles={['supporter', 'org_admin', 'coach']}>
          <CoachAssignmentsPage />
        </ProtectedRoute>
      } />

      {/* Admin Routes - New Admin Console */}
      <Route path="/admin" element={<AdminOverviewPage />} />
      <Route path="/admin/users" element={<AdminUsersPage />} />
      <Route path="/admin/users/:userId" element={<AdminUserDetailPage />} />
      <Route path="/admin/organizations" element={<AdminOrganizationsPage />} />
      <Route path="/admin/analytics" element={<AdminOverviewPage />} />
      <Route path="/admin/revenue" element={<AdminOverviewPage />} />
      <Route path="/admin/settings" element={<AdminSettingsPage />} />
      <Route path="/admin/chat-analytics" element={<ChatAnalyticsPage />} />
      <Route path="/admin/issues" element={<ReportIssuePage />} />
      <Route path="/admin/support-tickets" element={<AdminSupportTicketsPage />} />
      {/* Legacy admin route */}
      <Route path="/admin-legacy" element={<AdminDashboard />} />

      {/* Issue Tracking */}
      <Route path="/report-issue" element={
        <ProtectedRoute allowedRoles={['athlete', 'supporter', 'org_admin']}>
          <ReportIssuePage />
        </ProtectedRoute>
      } />

      {/* Matrix Leaderboard */}
      <Route path="/matrix-leaderboard" element={
        <ProtectedRoute allowedRoles={['athlete']}>
          <MatrixLeaderboardPage />
        </ProtectedRoute>
      } />

      {/* Help Center - accessible by all authenticated users */}
      <Route path="/help" element={
        <ProtectedRoute allowedRoles={['athlete', 'supporter', 'org_admin']}>
          <HelpCenterPage />
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ExerciseLibraryProvider>
          <div className="App min-h-screen bg-aura-bg">
            <AppRouter />
            <Toaster position="top-right" richColors />
            <SupportChatWidget />
          </div>
        </ExerciseLibraryProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
